import React from 'react';
import { FieldArray, FormikProps } from 'formik';
import { Flex, Label, Box, Field, Container } from 'theme-ui';
import { GrayFormikField } from '@/components/GrayFormikField';
import { TrashIcon } from '@heroicons/react/outline';
import Button from '@/components/Button';
import CustomDatePicker from '@/components/CustomDatePicker';
import Error from '@/components/Error';
import { PostInvoiceJobQuery } from '@/types/types';

interface IncentiveFormMainProps extends FormikProps<PostInvoiceJobQuery> {
    additionalErrors?: React.ReactNode;
    addToFooter?: React.ReactNode;
}

export const MainForm: React.FC<IncentiveFormMainProps> = (props) => {
    return (
        <Flex sx={{ flexDirection: 'column', gap: 3 }}>
            <Box>
                <Label htmlFor="InvoiceDate">Invoice date</Label>
                <CustomDatePicker
                    id="InvoiceDate"
                    variation="normal"
                    field={{
                        name: 'InvoiceDate',
                        value: !props.values.InvoiceDate
                            ? null
                            : new Date(props.values.InvoiceDate),
                    }}
                    onChange={(value) => {
                        props.setFieldValue('InvoiceDate', value.toISOString());
                    }}
                />
            </Box>

            <Box>
                <Label htmlFor="ScheduledStartTime">Scheduled start time</Label>
                <CustomDatePicker
                    id="ScheduledStartTime"
                    variation="normal"
                    field={{
                        name: 'ScheduledStartTime',
                        value: !props.values.ScheduledStartTime
                            ? null
                            : new Date(props.values.ScheduledStartTime),
                    }}
                    onChange={(value) => {
                        props.setFieldValue('ScheduledStartTime', value.toISOString());
                    }}
                />
            </Box>

            <Box>
                <Label>Company Ids</Label>
                <FieldArray
                    name="companyIds"
                    render={({ insert, remove, name }) => {
                        return (
                            <>
                                {props.values.companyIds.length === 0 ? (
                                    <Button
                                        variant="secondary"
                                        type="button"
                                        onClick={() => insert(0, '')}
                                    >
                                        Add company Id
                                    </Button>
                                ) : (
                                    props.values.companyIds.map((companyId, index) => {
                                        return (
                                            <>
                                                <Flex
                                                    sx={{
                                                        alignItems: 'center',
                                                        gap: 2,
                                                    }}
                                                    key={index}
                                                >
                                                    <Box
                                                        sx={{
                                                            flexGrow: 1,
                                                        }}
                                                    >
                                                        <GrayFormikField
                                                            name={`companyIds.${index}`}
                                                        />
                                                    </Box>
                                                    <Button
                                                        variant="danger"
                                                        type="button"
                                                        onClick={() => remove(index)} // remove a friend from the list
                                                    >
                                                        <TrashIcon width={16} height={16} />
                                                    </Button>
                                                </Flex>
                                                {props.values.companyIds.length === index + 1 ? (
                                                    <Button
                                                        variant="secondary"
                                                        type="button"
                                                        onClick={() => insert(index + 1, '')}
                                                    >
                                                        Add company Id
                                                    </Button>
                                                ) : null}
                                            </>
                                        );
                                    })
                                )}
                            </>
                        );
                    }}
                />
            </Box>

            <GrayFormikField type="checkbox" label="Only usage" name="OnlyUsage" />

            <GrayFormikField
                name="RecurringSchedule"
                type="text"
                label="Recurring schedule CRON expression"
            />
        </Flex>
    );
};

const CreateInvoiceJobForm: React.FC<IncentiveFormMainProps> = ({
    additionalErrors,
    addToFooter,
    ...formProps
}) => (
    <Flex backgroundColor="Background" sx={{ flexDirection: 'column', borderRadius: 'sm' }}>
        <Box p="3">
            <MainForm {...formProps} />
        </Box>

        {additionalErrors ? (
            <Box p="2">
                <Container variant="error-message">
                    <Error error={additionalErrors as any} />
                </Container>
            </Box>
        ) : null}
        <Flex
            p="2"
            sx={{
                width: '100%',
                gap: 2,
                justifyContent: 'flex-end',
            }}
        >
            {addToFooter}
            <Button
                isLoading={formProps.isSubmitting}
                disabled={formProps.isSubmitting}
                type="submit"
                onClick={formProps.submitForm}
            >
                Save
            </Button>
        </Flex>
    </Flex>
);

export default CreateInvoiceJobForm;
